/**
 * An HTML element tree built the way a browser nests tags — void elements never open,
 * optional end tags (`</p>`, `</li>`, `</td>`…) are implied where HTML implies them —
 * plus a list of the places where the markup is actually broken, with line numbers.
 */

import {
  type HtmlAttribute,
  type HtmlToken,
  tokenizeHtml,
} from "@/lib/tool-kit/developer/html/tokenize";

export const VOID_ELEMENTS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

/** Elements whose end tag HTML lets you leave out. */
const OPTIONAL_END = new Set([
  "body",
  "caption",
  "colgroup",
  "dd",
  "dt",
  "head",
  "html",
  "li",
  "optgroup",
  "option",
  "p",
  "rp",
  "rt",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
]);

/** Start tags that close an open `<p>` (when no table, button or the like is in between). */
const CLOSES_P = new Set([
  "address",
  "article",
  "aside",
  "blockquote",
  "details",
  "dialog",
  "div",
  "dl",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hgroup",
  "hr",
  "main",
  "menu",
  "nav",
  "ol",
  "p",
  "pre",
  "search",
  "section",
  "table",
  "ul",
]);

/** For each start tag, the open elements it closes and the elements that stop the search. */
const IMPLIED_CLOSE: Record<string, { closes: string[]; stops: string[] }> = {
  li: { closes: ["li"], stops: ["ul", "ol", "menu"] },
  dt: { closes: ["dt", "dd"], stops: ["dl"] },
  dd: { closes: ["dt", "dd"], stops: ["dl"] },
  tr: {
    closes: ["tr", "td", "th"],
    stops: ["table", "thead", "tbody", "tfoot"],
  },
  td: { closes: ["td", "th"], stops: ["tr", "table"] },
  th: { closes: ["td", "th"], stops: ["tr", "table"] },
  thead: {
    closes: [
      "thead",
      "tbody",
      "tfoot",
      "tr",
      "td",
      "th",
      "caption",
      "colgroup",
    ],
    stops: ["table"],
  },
  tbody: {
    closes: [
      "thead",
      "tbody",
      "tfoot",
      "tr",
      "td",
      "th",
      "caption",
      "colgroup",
    ],
    stops: ["table"],
  },
  tfoot: {
    closes: [
      "thead",
      "tbody",
      "tfoot",
      "tr",
      "td",
      "th",
      "caption",
      "colgroup",
    ],
    stops: ["table"],
  },
  option: { closes: ["option"], stops: ["select", "datalist", "optgroup"] },
  optgroup: { closes: ["option", "optgroup"], stops: ["select"] },
  rt: { closes: ["rt", "rp"], stops: ["ruby"] },
  rp: { closes: ["rt", "rp"], stops: ["ruby"] },
  body: { closes: ["head"], stops: ["html"] },
};

/** A `<p>` behind one of these is out of reach of a block start tag. */
const P_SCOPE_BOUNDARIES: readonly string[] = [
  "applet",
  "button",
  "caption",
  "html",
  "marquee",
  "object",
  "table",
  "td",
  "template",
  "th",
];

const FOREIGN_ROOTS = new Set(["svg", "math"]);

const RE_SOURCE_NAME = /^<([^\s/>]+)/;
const RE_INCOMPLETE_NAME = /^<\/?([^\s/>]*)/;

export interface ElementNode {
  kind: "element";
  /** Lower-cased. */
  name: string;
  /** The name as written, for output that keeps the author's case. */
  sourceName: string;
  attrs: HtmlAttribute[];
  /** Written as `<x/>`. */
  selfClosing: boolean;
  children: HtmlNode[];
  /** The element had its own end tag in the source. */
  hasEndTag: boolean;
  /** Source offsets of the whole element, and of the content between its tags. */
  start: number;
  end: number;
  innerStart: number;
  innerEnd: number;
  line: number;
  foreign: boolean;
}

export type HtmlNode =
  | ElementNode
  | { kind: "text"; raw: string }
  | { kind: "comment"; raw: string }
  | { kind: "declaration"; raw: string }
  | { kind: "rawtext"; raw: string }
  /** An end tag with nothing to close, kept where it was written. */
  | { kind: "stray"; raw: string };

export type HtmlIssue =
  /** An element whose end tag is required never got one. */
  | { kind: "unclosed"; tag: string; line: number }
  /** An end tag with no open element to close. */
  | { kind: "stray"; tag: string; line: number }
  /** `</b>` arrived while `<i>` (opened later) was still open: `<b><i></b>`. */
  | {
      kind: "misnested";
      tag: string;
      line: number;
      inner: string;
      innerLine: number;
    }
  /** A tag or comment that runs off the end of the input. */
  | { kind: "unterminated"; tag: string; line: number }
  /** `<div/>`: the slash means nothing on a non-void HTML element. */
  | { kind: "selfClosing"; tag: string; line: number };

export interface HtmlDocument {
  root: ElementNode;
  issues: HtmlIssue[];
}

/** 1-based line number of each offset, by binary search over the newline positions. */
function lineIndex(input: string): (offset: number) => number {
  const breaks: number[] = [];
  let index = input.indexOf("\n");
  while (index !== -1) {
    breaks.push(index);
    index = input.indexOf("\n", index + 1);
  }
  return (offset: number) => {
    let low = 0;
    let high = breaks.length;
    while (low < high) {
      const middle = Math.floor((low + high) / 2);
      if (breaks[middle] < offset) {
        low = middle + 1;
      } else {
        high = middle;
      }
    }
    return low + 1;
  };
}

function makeElement(
  token: Extract<HtmlToken, { kind: "start" }>,
  line: number,
  foreign: boolean
): ElementNode {
  const match = RE_SOURCE_NAME.exec(token.raw);
  return {
    kind: "element",
    name: token.name,
    sourceName: match ? match[1] : token.name,
    attrs: token.attrs,
    selfClosing: token.selfClosing,
    children: [],
    hasEndTag: false,
    start: token.start,
    end: token.end,
    innerStart: token.end,
    innerEnd: token.end,
    line,
    foreign,
  };
}

class TreeBuilder {
  readonly root: ElementNode;
  readonly stack: ElementNode[];
  /** How many elements of each name are open — so a search for one that is not is free. */
  readonly openCounts = new Map<string, number>();
  readonly issues: HtmlIssue[] = [];
  readonly lineAt: (offset: number) => number;

  constructor(input: string) {
    this.lineAt = lineIndex(input);
    this.root = {
      kind: "element",
      name: "#root",
      sourceName: "",
      attrs: [],
      selfClosing: false,
      children: [],
      hasEndTag: true,
      start: 0,
      end: input.length,
      innerStart: 0,
      innerEnd: input.length,
      line: 1,
      foreign: false,
    };
    this.stack = [this.root];
  }

  get top(): ElementNode {
    return this.stack.at(-1) ?? this.root;
  }

  /** Close the top element without an end tag of its own, at `offset`. */
  push(element: ElementNode): void {
    this.stack.push(element);
    this.openCounts.set(
      element.name,
      (this.openCounts.get(element.name) ?? 0) + 1
    );
  }

  popImplied(offset: number): void {
    const element = this.stack.pop();
    if (element) {
      element.innerEnd = offset;
      element.end = offset;
      this.openCounts.set(
        element.name,
        (this.openCounts.get(element.name) ?? 1) - 1
      );
    }
  }

  closeImplied(name: string, offset: number): void {
    const rule = IMPLIED_CLOSE[name];
    if (rule) {
      const at = this.findOpen(rule.closes, rule.stops);
      if (at !== -1) {
        this.closeDownTo(at, offset, null);
      }
    }
    if (CLOSES_P.has(name)) {
      const at = this.findOpen(["p"], P_SCOPE_BOUNDARIES);
      if (at !== -1) {
        this.closeDownTo(at, offset, null);
      }
    }
  }

  /** Index in the stack of the nearest open element named in `names`, or -1. */
  findOpen(names: readonly string[], stops: readonly string[]): number {
    if (!names.some((name) => (this.openCounts.get(name) ?? 0) > 0)) {
      return -1;
    }
    for (let index = this.stack.length - 1; index > 0; index -= 1) {
      const name = this.stack[index].name;
      if (names.includes(name)) {
        return index;
      }
      if (stops.includes(name)) {
        return -1;
      }
    }
    return -1;
  }

  /**
   * Pop everything above stack index `at`, then `at` itself. Elements above it whose
   * end tag is required are reported as misnested against `closing` (when an end tag
   * caused this) — implied closes report nothing.
   */
  closeDownTo(
    at: number,
    offset: number,
    closing: { tag: string; line: number } | null
  ): void {
    while (this.stack.length - 1 > at) {
      const inner = this.top;
      if (closing && !OPTIONAL_END.has(inner.name) && !inner.foreign) {
        this.issues.push({
          kind: "misnested",
          tag: closing.tag,
          line: closing.line,
          inner: inner.name,
          innerLine: inner.line,
        });
      }
      this.popImplied(offset);
    }
    this.popImplied(offset);
  }

  start(token: Extract<HtmlToken, { kind: "start" }>): void {
    const foreign = this.top.foreign || FOREIGN_ROOTS.has(token.name);
    if (!foreign) {
      this.closeImplied(token.name, token.start);
    }
    const element = makeElement(token, this.lineAt(token.start), foreign);
    this.top.children.push(element);
    if (VOID_ELEMENTS.has(token.name)) {
      return;
    }
    if (token.selfClosing) {
      if (!foreign) {
        this.issues.push({
          kind: "selfClosing",
          tag: token.name,
          line: element.line,
        });
      }
      return;
    }
    this.push(element);
  }

  end(token: Extract<HtmlToken, { kind: "end" }>): void {
    const line = this.lineAt(token.start);
    const at = VOID_ELEMENTS.has(token.name)
      ? -1
      : this.findOpen([token.name], []);
    if (at === -1) {
      // Browsers ignore most stray end tags but turn </br> into <br> and </p> into an
      // empty paragraph, so it stays in the output exactly as written.
      this.issues.push({ kind: "stray", tag: token.name, line });
      this.top.children.push({ kind: "stray", raw: token.raw });
      return;
    }
    const element = this.stack[at];
    this.closeDownTo(at, token.start, { tag: token.name, line });
    element.hasEndTag = true;
    element.innerEnd = token.start;
    element.end = token.end;
  }

  add(token: HtmlToken): void {
    switch (token.kind) {
      case "start":
        this.start(token);
        break;
      case "end":
        this.end(token);
        break;
      case "comment":
        if (!token.terminated) {
          this.issues.push({
            kind: "unterminated",
            tag: "!--",
            line: this.lineAt(token.start),
          });
        }
        this.top.children.push({ kind: "comment", raw: token.raw });
        break;
      case "declaration":
        this.top.children.push({ kind: "declaration", raw: token.raw });
        break;
      case "rawtext":
        this.top.children.push({ kind: "rawtext", raw: token.raw });
        break;
      case "incomplete":
        this.issues.push({
          kind: "unterminated",
          tag: RE_INCOMPLETE_NAME.exec(token.raw)?.[1] ?? "",
          line: this.lineAt(token.start),
        });
        this.top.children.push({ kind: "text", raw: token.raw });
        break;
      default:
        this.top.children.push({ kind: "text", raw: token.raw });
    }
  }

  finish(inputLength: number): HtmlDocument {
    while (this.stack.length > 1) {
      const element = this.top;
      if (!(OPTIONAL_END.has(element.name) || element.foreign)) {
        this.issues.push({
          kind: "unclosed",
          tag: element.name,
          line: element.line,
        });
      }
      this.popImplied(inputLength);
    }
    this.issues.sort((a, b) => a.line - b.line);
    return { root: this.root, issues: this.issues };
  }
}

export function parseHtmlDocument(input: string): HtmlDocument {
  const builder = new TreeBuilder(input);
  for (const token of tokenizeHtml(input)) {
    builder.add(token);
  }
  return builder.finish(input.length);
}
